import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FileText, ExternalLink, CheckCircle2, XCircle, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const CHECK_LABELS = { name: 'Name', phone: 'Mobile number', reference: 'Reference number' };

export default function AdminProviderDetail() {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [fieldNotes, setFieldNotes] = useState({});

  const load = async () => {
    const { data } = await api.get('/admin/providers/' + id);
    setProvider(data.provider);
    setDocs(data.documents || []);
    setSummary(data.verification_summary || null);
    setDuplicates(data.duplicate_contacts || null);
  };

  useEffect(() => { load().catch(() => {}); }, [id]);

  const setVerification = async (check, verified) => {
    const status = verified ? 'verified' : 'resubmit';
    const note = (fieldNotes[check] || '').trim();

    if (status === 'resubmit' && !note) {
      toast.error('Add a note explaining what needs to be changed for ' + CHECK_LABELS[check].toLowerCase());
      return;
    }
    if (check === 'reference' && status === 'verified' && !note) {
      toast.error('Reference verification note is required');
      return;
    }

    try {
      await api.post('/admin/providers/' + id + '/verification', { check, status, note });
      toast.success(CHECK_LABELS[check] + (verified ? ' verified' : ' sent back for resubmission'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update verification');
    }
  };

  const setDocumentDecision = async (docId, status) => {
    const note = (fieldNotes['doc:' + docId] || '').trim();
    if (status === 'resubmit' && !note) {
      toast.error('Add a note explaining what needs to be corrected in this document');
      return;
    }

    try {
      await api.post('/admin/providers/' + id + '/documents/' + docId + '/decision', {
        status,
        note,
      });
      toast.success(status === 'approved' ? 'Document approved' : 'Document sent back for resubmission');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update document');
    }
  };

  const decide = async (status) => {
    const reason = decisionNote.trim();
    if (status === 'rejected' && !reason) {
      toast.error('Rejection reason is required');
      return;
    }
    if (status === 'resubmit' && !reason) {
      toast.error('Add a note explaining what must be resubmitted');
      return;
    }

    try {
      await api.post('/admin/providers/' + id + '/decision', { status, reason });
      toast.success(status === 'approved' ? 'Application approved' : 'Application marked ' + status);
      await load();
      setDecisionNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update application');
    }
  };

  if (!provider) {
    return (
      <MobileShell title="Loading...">
        <div className="p-5 text-sm text-slate-500">Loading...</div>
      </MobileShell>
    );
  }

  const checks = provider.verification?.checks || {};
  const canApprove = !!summary?.can_approve;
  const identityComplete = !!summary?.identity_complete;
  const duplicateProviderMatches = duplicates?.provider_matches || [];
  const duplicateUserMatches = duplicates?.user_matches || [];

  return (
    <MobileShell title="Ranger Verification">
      <div className="px-5 py-5 space-y-4">
        {duplicates?.has_duplicates && (
          <div className="resqly-card p-4 border-amber-200 bg-amber-50">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Duplicate contact warning</h3>
                <p className="text-xs text-amber-800 mt-1">
                  The same mobile number or email is already present on another RESQLY account.
                  This is a warning for admin review, not an automatic rejection.
                </p>
                <div className="mt-3 space-y-2">
                  {duplicateProviderMatches.map((p) => (
                    <div key={'provider-' + p.id} className="text-xs border border-amber-200 rounded-lg p-2 bg-white">
                      <div className="font-semibold text-slate-900">
                        {p.name || 'Unnamed'} · {p.category?.replace('_', ' ') || 'No service'}
                      </div>
                      <div className="text-slate-600">
                        {p.phone || 'No mobile'} · {p.email || 'No email'} · {p.approval_status || 'unknown'}
                      </div>
                    </div>
                  ))}
                  {duplicateUserMatches.map((u) => (
                    <div key={'user-' + u.id} className="text-xs border border-amber-200 rounded-lg p-2 bg-white">
                      <div className="font-semibold text-slate-900">
                        {u.name || 'Unnamed consumer'} · Consumer account
                      </div>
                      <div className="text-slate-600">
                        {u.phone || 'No mobile'} · {u.email || 'No email'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="resqly-card p-5">
          <div className="flex justify-between gap-3">
            <div>
              <div className="font-bold text-lg text-slate-900">{provider.name || 'Unnamed'}</div>
              <div className="text-xs text-slate-500 capitalize">
                {provider.category?.replace('_', ' ') || 'No category'}
              </div>
            </div>
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-700 capitalize self-start">
              {provider.approval_status}
            </span>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <Row label="Mobile" value={provider.phone} />
            <Row label="Email" value={provider.email} />
            <Row label="City" value={provider.city} />
            <Row label="Current address" value={provider.current_address} />
            <Row label="Reference number" value={provider.reference_number} />
            <Row label="Service area" value={provider.service_area} />
            <Row label="Gender" value={provider.gender} />
            <Row label="Experience" value={provider.experience_years != null ? provider.experience_years + ' years' : ''} />
            <Row label="Languages" value={(provider.languages || []).join(', ')} />
            <Row label="Specializations" value={(provider.specialization || []).join(', ')} />
            <Row label="Description" value={provider.description} />
            <Row label="Bank account holder" value={provider.bank_account_holder} />
            <Row label="Bank name" value={provider.bank_name} />
            <Row label="Bank account number" value={provider.bank_account_number} />
            <Row label="IFSC" value={provider.bank_ifsc} />
            <Row label="Profile completion" value={(provider.profile_completion || 0) + '%'} />
          </div>
        </div>

        <div className="resqly-card p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-700" />
            <h3 className="font-semibold text-slate-900">Step 1 — Name, Mobile & Reference</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            All three checks must be green before document verification can begin.
          </p>

          <div className="mt-4 space-y-3">
            {Object.entries(CHECK_LABELS).map(([key, label]) => (
              <div
                key={key}
                className={
                  'rounded-lg border p-3 ' +
                  (checks[key]?.status === 'verified'
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : checks[key]?.status === 'resubmit'
                      ? 'border-rose-200 bg-rose-50/40'
                      : 'border-slate-200')
                }
              >
                <div className="flex items-center gap-3">
                  <input
                    aria-label={'Verify ' + label}
                    type="checkbox"
                    checked={checks[key]?.status === 'verified'}
                    onChange={(e) => setVerification(key, e.target.checked)}
                    className="w-5 h-5 accent-emerald-600"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{label}</div>
                    <div className="text-xs text-slate-500 capitalize">
                      {checks[key]?.status || 'pending'}
                    </div>
                  </div>
                  {checks[key]?.status === 'verified'
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    : <XCircle className="w-5 h-5 text-rose-500" />}
                </div>

                <Textarea
                  value={fieldNotes[key] ?? checks[key]?.note ?? ''}
                  onChange={(e) => setFieldNotes({ ...fieldNotes, [key]: e.target.value })}
                  placeholder={
                    key === 'reference'
                      ? 'Reference note — whose reference number is this? (required to verify)'
                      : 'Optional now; required when sending ' + label.toLowerCase() + ' back'
                  }
                  rows={2}
                  className="mt-2"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="resqly-card p-5">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Step 2 — Documents</h3>
          </div>

          <p className="text-xs text-slate-500 mt-1">
            Documents unlock only after Name, Mobile and Reference are verified.
            Resubmitting one file does not reset the others.
          </p>

          {!identityComplete && (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Complete Step 1 to unlock document verification.
            </div>
          )}

          <div className={'mt-3 space-y-3 ' + (!identityComplete ? 'opacity-60' : '')}>
            {docs.length === 0 && (
              <p className="text-sm text-slate-500">No documents uploaded yet.</p>
            )}

            {docs.map((d) => (
              <div
                key={d.id}
                className={
                  'border rounded-lg p-3 ' +
                  (d.verification_status === 'approved'
                    ? 'border-emerald-200 bg-emerald-50/30'
                    : (d.verification_status === 'resubmit' || d.verification_status === 'rejected')
                      ? 'border-rose-200 bg-rose-50/30'
                      : 'border-slate-200')
                }
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-slate-500 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{d.document_type}</div>
                    <div className="text-xs text-slate-500">
                      {d.file_name || 'Uploaded'} · {d.verification_status}
                    </div>
                    {d.admin_note && (
                      <p className="text-xs text-rose-700 mt-1">{d.admin_note}</p>
                    )}
                  </div>
                  {d.document_url && (
                    <a
                      data-testid={'admin-view-' + d.id}
                      href={d.document_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 text-xs inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  )}
                </div>

                <Textarea
                  value={fieldNotes['doc:' + d.id] ?? d.admin_note ?? ''}
                  onChange={(e) => setFieldNotes({ ...fieldNotes, ['doc:' + d.id]: e.target.value })}
                  placeholder="Note required when asking for this document again"
                  rows={2}
                  className="mt-2"
                />

                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    disabled={!identityComplete}
                    onClick={() => setDocumentDecision(d.id, 'approved')}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    disabled={!identityComplete}
                    onClick={() => setDocumentDecision(d.id, 'resubmit')}
                    variant="outline"
                    className="border-rose-200 text-rose-700 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Request resubmission
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="resqly-card p-5">
          <h3 className="font-semibold text-slate-900">Final Application Decision</h3>

          <div className="mt-3 text-xs space-y-1">
            <div className={summary?.identity_complete ? 'text-emerald-700' : 'text-amber-700'}>
              Identity checks: {summary?.identity_complete ? 'Complete' : 'Missing ' + (summary?.missing_check_labels?.join(', ') || 'checks')}
            </div>
            <div className={summary?.documents_complete ? 'text-emerald-700' : 'text-amber-700'}>
              Documents: {summary?.documents_complete ? 'All required documents approved' : 'Pending / resubmission: ' + (summary?.missing_documents?.join(', ') || 'some files')}
            </div>
          </div>

          <Textarea
            data-testid="admin-reason"
            value={decisionNote}
            onChange={(e) => setDecisionNote(e.target.value)}
            placeholder="For rejection: enter the reason. For resubmission: explain what they must change."
            className="mt-3"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
            <Button
              data-testid="admin-approve"
              disabled={!canApprove}
              onClick={() => decide('approved')}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> {canApprove ? 'Approve' : 'Complete verification first'}
            </Button>
            <Button
              data-testid="admin-resubmit"
              onClick={() => decide('resubmit')}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Resubmit application
            </Button>
            <Button
              data-testid="admin-reject"
              onClick={() => decide('rejected')}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject application
            </Button>
          </div>
        </div>
      </div>
    </MobileShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right break-words">{value || '—'}</span>
    </div>
  );
}
